import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/api';
import {
  BarChart3, TrendingUp, DollarSign, MousePointerClick, Eye, MessageSquare,
  Users, Target, Sparkles, ChevronDown, RefreshCw, Send, Loader2
} from 'lucide-react';

const API_URL = getApiUrl();

const MarketingIntelPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState('last_30d');
  const [activeTab, setActiveTab] = useState('campaigns');
  const [adsetData, setAdsetData] = useState(null);
  const [adData, setAdData] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiQuestion, setAiQuestion] = useState('');
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('all');

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const resp = await axios.get(`${API_URL}/api/marketing-intel/ad-accounts`, { withCredentials: true, headers });
        setAccounts(resp.data.accounts || []);
      } catch (err) { console.error(err); }
    };
    fetchAccounts();
  }, []);

  const fetchCampaignData = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await axios.get(`${API_URL}/api/marketing-intel/campaign-performance?date_preset=${datePreset}&account_id=${selectedAccount}`, { withCredentials: true, headers });
      setData(resp.data);
    } catch (err) {
      console.error('Error fetching campaign data:', err);
    } finally {
      setLoading(false);
    }
  }, [datePreset, selectedAccount]);

  useEffect(() => { fetchCampaignData(); }, [fetchCampaignData]);

  const fetchAdsets = async () => {
    try {
      setAdsetData(null);
      const resp = await axios.get(`${API_URL}/api/marketing-intel/adset-performance?date_preset=${datePreset}&account_id=${selectedAccount}`, { withCredentials: true, headers });
      setAdsetData(resp.data);
    } catch (err) { console.error(err); }
  };

  const fetchAds = async () => {
    try {
      setAdData(null);
      const resp = await axios.get(`${API_URL}/api/marketing-intel/ad-performance?date_preset=${datePreset}&account_id=${selectedAccount}`, { withCredentials: true, headers });
      setAdData(resp.data);
    } catch (err) { console.error(err); }
  };

  const runAIAnalysis = async () => {
    try {
      setAiLoading(true);
      const resp = await axios.post(`${API_URL}/api/marketing-intel/ai-analysis`, { question: aiQuestion, date_preset: datePreset, account_id: selectedAccount }, { withCredentials: true, headers });
      setAiAnalysis(resp.data.analysis);
    } catch (err) { console.error(err); setAiAnalysis('Failed to get AI analysis.'); }
    finally { setAiLoading(false); }
  };

  useEffect(() => {
    if (activeTab === 'adsets') fetchAdsets();
    if (activeTab === 'ads') fetchAds();
  }, [activeTab, datePreset, selectedAccount]);

  const s = data?.summary || {};

  const tabs = [
    { id: 'campaigns', label: 'Campaigns' },
    { id: 'adsets', label: 'Ad Sets' },
    { id: 'ads', label: 'Ads' },
    { id: 'ai', label: 'AI Strategy', icon: Sparkles },
  ];

  const presets = [
    { value: 'last_7d', label: '7D' },
    { value: 'last_14d', label: '14D' },
    { value: 'last_30d', label: '30D' },
    { value: 'last_90d', label: '90D' },
  ];

  return (
    <div className="p-6 space-y-6" data-testid="marketing-intel-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Marketing Intelligence</h1>
          <p className="text-sm text-slate-500 mt-1">Meta Ads performance + AI-powered strategy</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Account Selector */}
          {accounts.length > 0 && (
            <div className="relative">
              <select
                value={selectedAccount}
                onChange={e => { setSelectedAccount(e.target.value); setAdsetData(null); setAdData(null); }}
                data-testid="intel-account-selector"
                className="appearance-none bg-white border border-slate-200 rounded-lg px-3 py-1.5 pr-8 text-sm text-slate-700 cursor-pointer font-medium"
              >
                <option value="all">All Accounts</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name || a.id}</option>)}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            </div>
          )}
          {/* Period Selector */}
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
            {presets.map(p => (
              <button key={p.value} onClick={() => { setDatePreset(p.value); setAdsetData(null); setAdData(null); }}
                data-testid={`intel-period-${p.label.toLowerCase()}`}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${datePreset === p.value ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <button onClick={fetchCampaignData} className="p-2 rounded-lg bg-white border border-slate-200 hover:bg-slate-50" data-testid="intel-refresh">
            <RefreshCw className={`w-4 h-4 text-slate-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          <MetricCard label="Total Spend" value={`₹${(s.total_spend || 0).toLocaleString('en-IN')}`} icon={DollarSign} />
          <MetricCard label="Impressions" value={(s.total_impressions || 0).toLocaleString()} icon={Eye} />
          <MetricCard label="Clicks" value={(s.total_clicks || 0).toLocaleString()} icon={MousePointerClick} />
          <MetricCard label="Leads" value={s.total_leads || 0} icon={Users} />
          <MetricCard label="Messages" value={s.total_messages || 0} icon={MessageSquare} />
          <MetricCard label="Avg CPA" value={s.avg_cpa ? `₹${s.avg_cpa.toLocaleString('en-IN')}` : '-'} icon={Target} />
          <MetricCard label="Avg CTR" value={s.avg_ctr ? `${s.avg_ctr}%` : '-'} icon={TrendingUp} />
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              data-testid={`intel-tab-${tab.id}`}
              className={`flex items-center gap-2 pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {tab.icon && <tab.icon className="w-4 h-4" />}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {loading && !data ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      ) : (
        <>
          {activeTab === 'campaigns' && <CampaignsTable campaigns={data?.campaigns || []} />}
          {activeTab === 'adsets' && <AdsetsTable adsets={adsetData?.adsets || []} loading={!adsetData} />}
          {activeTab === 'ads' && <AdsTable ads={adData?.ads || []} loading={!adData} />}
          {activeTab === 'ai' && (
            <AIStrategyPanel
              analysis={aiAnalysis} loading={aiLoading}
              question={aiQuestion} setQuestion={setAiQuestion}
              onAnalyze={runAIAnalysis}
            />
          )}
        </>
      )}
    </div>
  );
};

// --- Sub Components ---

const MetricCard = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-3">
    <div className="flex items-center justify-between mb-1">
      <span className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">{label}</span>
      <Icon className="w-3.5 h-3.5 text-slate-300" />
    </div>
    <p className="text-lg font-bold text-slate-900">{value}</p>
  </div>
);

const CampaignsTable = ({ campaigns }) => (
  <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="campaigns-performance-table">
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50 text-left text-xs text-slate-500">
            <th className="p-3 font-medium">Campaign</th>
            <th className="p-3 font-medium text-right">Spend</th>
            <th className="p-3 font-medium text-right">Impressions</th>
            <th className="p-3 font-medium text-right">Clicks</th>
            <th className="p-3 font-medium text-right">CTR</th>
            <th className="p-3 font-medium text-right">CPC</th>
            <th className="p-3 font-medium text-right">Leads</th>
            <th className="p-3 font-medium text-right">Messages</th>
            <th className="p-3 font-medium text-right">CPA</th>
          </tr>
        </thead>
        <tbody>
          {campaigns.map(c => (
            <tr key={c.campaign_id} className="border-t border-slate-100 hover:bg-slate-50">
              <td className="p-3 font-medium text-slate-800 max-w-xs truncate">{c.campaign_name}</td>
              <td className="p-3 text-right font-medium text-slate-700">₹{c.spend.toLocaleString('en-IN')}</td>
              <td className="p-3 text-right text-slate-600">{c.impressions.toLocaleString()}</td>
              <td className="p-3 text-right text-slate-600">{c.clicks.toLocaleString()}</td>
              <td className="p-3 text-right text-slate-600">{c.ctr}%</td>
              <td className="p-3 text-right text-slate-600">₹{c.cpc}</td>
              <td className="p-3 text-right">
                <span className={`font-bold ${c.leads > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>{c.leads}</span>
              </td>
              <td className="p-3 text-right">
                <span className={`font-medium ${c.messages > 0 ? 'text-blue-600' : 'text-slate-300'}`}>{c.messages}</span>
              </td>
              <td className="p-3 text-right">
                {c.cpa > 0 ? (
                  <span className={`font-bold ${c.cpa < 1000 ? 'text-emerald-600' : c.cpa < 3000 ? 'text-amber-600' : 'text-red-600'}`}>₹{c.cpa.toLocaleString('en-IN')}</span>
                ) : <span className="text-slate-300">-</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
    {campaigns.length === 0 && <div className="p-8 text-center text-slate-400"><BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-40" /><p>No campaign data for this period</p></div>}
  </div>
);

const AdsetsTable = ({ adsets, loading }) => {
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="adsets-performance-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs text-slate-500">
              <th className="p-3 font-medium">Ad Set</th>
              <th className="p-3 font-medium">Campaign</th>
              <th className="p-3 font-medium text-right">Spend</th>
              <th className="p-3 font-medium text-right">Clicks</th>
              <th className="p-3 font-medium text-right">CTR</th>
              <th className="p-3 font-medium text-right">Leads</th>
              <th className="p-3 font-medium text-right">Messages</th>
              <th className="p-3 font-medium text-right">CPA</th>
            </tr>
          </thead>
          <tbody>
            {adsets.map((a, i) => (
              <tr key={a.adset_id || i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-800 max-w-[200px] truncate">{a.adset_name}</td>
                <td className="p-3 text-slate-500 max-w-[200px] truncate text-xs">{a.campaign_name}</td>
                <td className="p-3 text-right font-medium text-slate-700">₹{a.spend.toLocaleString('en-IN')}</td>
                <td className="p-3 text-right text-slate-600">{a.clicks}</td>
                <td className="p-3 text-right text-slate-600">{a.ctr}%</td>
                <td className="p-3 text-right"><span className={`font-bold ${a.leads > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>{a.leads}</span></td>
                <td className="p-3 text-right"><span className={`font-medium ${a.messages > 0 ? 'text-blue-600' : 'text-slate-300'}`}>{a.messages}</span></td>
                <td className="p-3 text-right">{a.cpa > 0 ? <span className={`font-bold ${a.cpa < 1000 ? 'text-emerald-600' : a.cpa < 3000 ? 'text-amber-600' : 'text-red-600'}`}>₹{a.cpa.toLocaleString('en-IN')}</span> : <span className="text-slate-300">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {adsets.length === 0 && <div className="p-8 text-center text-slate-400"><p>No ad set data for this period</p></div>}
    </div>
  );
};

const AdsTable = ({ ads, loading }) => {
  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>;
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="ads-performance-table">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-left text-xs text-slate-500">
              <th className="p-3 font-medium">Ad</th>
              <th className="p-3 font-medium">Ad Set</th>
              <th className="p-3 font-medium text-right">Spend</th>
              <th className="p-3 font-medium text-right">Clicks</th>
              <th className="p-3 font-medium text-right">CTR</th>
              <th className="p-3 font-medium text-right">Leads</th>
              <th className="p-3 font-medium text-right">Messages</th>
              <th className="p-3 font-medium text-right">CPA</th>
            </tr>
          </thead>
          <tbody>
            {ads.map((a, i) => (
              <tr key={a.ad_id || i} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="p-3 font-medium text-slate-800 max-w-[200px] truncate">{a.ad_name}</td>
                <td className="p-3 text-slate-500 max-w-[200px] truncate text-xs">{a.adset_name}</td>
                <td className="p-3 text-right font-medium text-slate-700">₹{a.spend.toLocaleString('en-IN')}</td>
                <td className="p-3 text-right text-slate-600">{a.clicks}</td>
                <td className="p-3 text-right text-slate-600">{a.ctr}%</td>
                <td className="p-3 text-right"><span className={`font-bold ${a.leads > 0 ? 'text-emerald-600' : 'text-slate-300'}`}>{a.leads}</span></td>
                <td className="p-3 text-right"><span className={`font-medium ${a.messages > 0 ? 'text-blue-600' : 'text-slate-300'}`}>{a.messages}</span></td>
                <td className="p-3 text-right">{a.cpa > 0 ? <span className={`font-bold ${a.cpa < 1000 ? 'text-emerald-600' : a.cpa < 3000 ? 'text-amber-600' : 'text-red-600'}`}>₹{a.cpa.toLocaleString('en-IN')}</span> : <span className="text-slate-300">-</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {ads.length === 0 && <div className="p-8 text-center text-slate-400"><p>No ad data for this period</p></div>}
    </div>
  );
};

const AIStrategyPanel = ({ analysis, loading, question, setQuestion, onAnalyze }) => (
  <div className="space-y-4" data-testid="ai-strategy-panel">
    <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-200 p-6">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-5 h-5 text-indigo-600" />
        <h3 className="font-semibold text-indigo-900">AI Strategy Engine</h3>
        <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-700 rounded-full">Claude Sonnet</span>
      </div>
      <p className="text-sm text-indigo-700 mb-4">Ask AI to analyze your campaign performance and recommend optimizations.</p>
      <div className="flex gap-2">
        <input
          type="text"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          placeholder="e.g., Which campaigns should I scale? What's the best performing audience?"
          className="flex-1 px-4 py-2.5 border border-indigo-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 bg-white"
          data-testid="ai-question-input"
          onKeyDown={e => e.key === 'Enter' && !loading && onAnalyze()}
        />
        <button
          onClick={onAnalyze}
          disabled={loading}
          data-testid="ai-analyze-btn"
          className="px-5 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Analyze
        </button>
      </div>
    </div>

    {analysis && (
      <div className="bg-white rounded-xl border border-slate-200 p-6" data-testid="ai-analysis-result">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <h4 className="font-semibold text-slate-800">AI Recommendations</h4>
        </div>
        <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap">{analysis}</div>
      </div>
    )}
  </div>
);

export default MarketingIntelPage;
